import { useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { useAdImages } from '@/hooks/useAdImages'
import { useAdvertisementsBySupplierId } from '@/hooks/useAdvertisementsBySupplierId'
import { adImageService } from '@/services/ad_image_service'
import { AdImageWithTag } from '@magnetar/admin-api-interfaces'
import { PageHeader } from '@/components/PageHeader'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { DeleteAlert } from '@/components/DeleteAlert'
import { Plus, Settings, Trash2 } from 'lucide-react'
import UploadAdImageDialog from './UploadAdImageDialog'
import EditAdImageDialog from './EditAdImageDialog'
import { logger } from '@magnetar/utils'

const getImageUrl = (fileUuid: string) => `${import.meta.env.VITE_API_URL}/api/files/download/${fileUuid}`

export default function AdSupplierAdImagesPage() {
  const { mode, id: viewModeId } = useViewMode()
  const supplierId = mode === 'ad' ? (viewModeId ?? undefined) : undefined
  const { token } = useAuth()

  const { adImages, isLoading, mutate } = useAdImages(supplierId, { includeInactive: true })
  const { advertisements = [] } = useAdvertisementsBySupplierId(supplierId, '?page=1&pageSize=1000')

  const [uploadOpen, setUploadOpen] = useState(false)
  const [editingImage, setEditingImage] = useState<AdImageWithTag | null>(null)
  const [deletingImage, setDeletingImage] = useState<AdImageWithTag | null>(null)

  const handleToggleActive = async (image: AdImageWithTag) => {
    if (!supplierId || !token) return

    const response = await adImageService.updateAdImage(
      supplierId,
      image.ad_image_id,
      { is_active: !image.is_active },
      token,
    )

    if (response.success) {
      toast.success(image.is_active ? '已停用廣告圖片' : '已啟用廣告圖片')
      mutate()
    } else {
      toast.error(response.error?.message || '更新失敗')
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deletingImage || !supplierId || !token) return

    try {
      const response = await adImageService.deleteAdImage(
        supplierId,
        deletingImage.ad_image_id,
        token,
      )

      if (response.success) {
        toast.success('廣告圖片已刪除')
        mutate()
      } else {
        toast.error(response.error?.message || '刪除廣告圖片失敗')
      }
    } catch (error) {
      logger.error('刪除廣告圖片失敗:', error)
      toast.error('刪除廣告圖片失敗，請稍後再試')
    } finally {
      setDeletingImage(null)
    }
  }

  if (!supplierId) {
    return (
      <div className="space-y-6">
        <PageHeader title="廣告圖片" description="管理供應商的獨立廣告圖片" />
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">
            請先從供應商列表選擇一家供應商，才能管理其廣告圖片。
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="廣告圖片"
        description="上傳廣告圖片，並可標記對應的廣告，播放該廣告時將強制同時顯示此圖片"
      >
        <Button onClick={() => setUploadOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          上傳廣告圖片
        </Button>
      </PageHeader>

      {isLoading ? (
        <div className="p-6 text-center text-muted-foreground">載入中...</div>
      ) : adImages.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">尚未上傳任何廣告圖片</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {adImages.map((image) => (
            <Card key={image.ad_image_id} className="overflow-hidden">
              <img
                src={getImageUrl(image.file_uuid)}
                alt={`廣告圖片 ${image.ad_image_id}`}
                className="w-full h-40 object-cover"
              />
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">#{image.ad_image_id}</Badge>
                  <Switch
                    checked={image.is_active}
                    onCheckedChange={() => handleToggleActive(image)}
                  />
                </div>
                <div className="text-sm text-muted-foreground truncate">
                  {image.tag_ad
                    ? `標記廣告：#${image.tag_ad.advertisement_id} ${image.tag_ad.title || ''}`
                    : '未標記廣告'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {image.play_duration_ms
                    ? `播放時長：${Math.round(image.play_duration_ms / 1000)} 秒`
                    : '未設定播放時長'}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingImage(image)}>
                    <Settings className="mr-1 h-3 w-3" />
                    編輯
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeletingImage(image)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <UploadAdImageDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        supplierId={supplierId}
        onSuccess={() => {
          mutate()
          setUploadOpen(false)
        }}
      />

      <EditAdImageDialog
        open={editingImage !== null}
        onOpenChange={(open) => !open && setEditingImage(null)}
        supplierId={supplierId}
        image={editingImage}
        advertisements={advertisements}
        onSuccess={() => {
          mutate()
          setEditingImage(null)
        }}
      />

      <DeleteAlert
        open={deletingImage !== null}
        onOpenChange={(open) => !open && setDeletingImage(null)}
        onConfirm={handleDeleteConfirm}
        title="確定要刪除此廣告圖片嗎？"
        description="此操作無法復原，將永久刪除此圖片檔案。"
      />
    </div>
  )
}
